import React from 'react';
import UsingBlendedRateField from 'components/UsingBlendedRateField';
import MoneyField from 'components/MoneyField';
import { RateSettingsDisplayHelper, type W2Burdens } from 'utils/displayHelpers/rate-settings';
import { DisplayHelpers } from 'utils/helpers';
import { useRecordContext, useResourceContext } from 'ra-core';
import { BooleanField, FunctionField, NumberField, SimpleShowLayout } from 'react-admin';
import {
  calculatedFieldPath,
  isFieldVisible,
  sources,
  type RateSettingsField,
} from '../../utils/fieldsVisibility';
import { titleize } from 'inflection';
import PercentField from 'components/PercentField';
import ProRateField from 'components/ProRateField';
import Heading from 'components/Heading';
import { Alert } from '@medely/ui-kit/web';
import useCheckAccess from 'hooks/useCheckAccess';
import { W2BurdensTooltip } from 'resources/rate_settings/shared/W2BurdensTooltip';
import { Info } from '@mui/icons-material';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

type ChildrenRateSettingsProps = {
  shouldShowField: (field: RateSettingsField) => boolean;
  w2Burdens: W2Burdens;
};

type RateSettingsProps = {
  source: string;
  taxClassification: 'w2.local' | 'w2.travel' | 'ten99';
};

const FIELD_LABEL_OVERRIDES: Partial<Record<RateSettingsField, string>> = {
  base_charge_rate_cents: 'Base Charge Rate',
};

const RateSettingsSpecificFields = ({
  source,
  shouldShowField,
  taxClassification,
  w2Burdens,
}: RateSettingsProps & ChildrenRateSettingsProps) => {
  const record = useRecordContext();
  const fields = [];

  sources[taxClassification].forEach((fieldSource) => {
    if (shouldShowField(fieldSource as RateSettingsField)) {
      const fieldPath = calculatedFieldPath(`${source}.${taxClassification}`, fieldSource, (path) =>
        get(record, path),
      );

      const fieldLabel =
        FIELD_LABEL_OVERRIDES[fieldSource as RateSettingsField] ?? titleize(fieldSource);
      const FieldComponent = fieldSource.includes('cents') ? MoneyField : PercentField;

      if (fieldSource === 'total_margin_percent_w2') {
        const fieldValue = get(record, fieldPath);
        const hasFieldValue = !isNil(fieldValue);
        const showTooltip = hasFieldValue && !!w2Burdens;

        fields.push(
          <PercentField
            key={fieldSource}
            source={fieldPath}
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {fieldLabel}
                {showTooltip && (
                  <W2BurdensTooltip w2Burdens={w2Burdens} enabled={!!w2Burdens}>
                    <Info
                      style={{
                        fontSize: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        lineHeight: 0,
                      }}
                    />
                  </W2BurdensTooltip>
                )}
              </span>
            }
            blankIfNull
          />,
        );
      } else {
        fields.push(
          <FieldComponent key={fieldSource} source={fieldPath} label={fieldLabel} blankIfNull />,
        );
      }
    }
  });

  return fields;
};

const ScalingMarginsFields = (taxClassification: 'ten99' | 'w2.local' | 'w2.travel') => {
  const fields = [];
  const key = taxClassification.includes('w2') ? 'w2' : '1099';
  const settings_subhash_key = taxClassification.includes('w2') ? 'w2' : 'ten99';
  const resource = useResourceContext();
  const checkAccess = useCheckAccess();
  const isMPRS = resource === 'MarketPositionRateSetting';
  const isLPRS = resource === 'LocationPositionRateSetting';

  if ((taxClassification === 'w2.travel' && !isLPRS) || (!isMPRS && !isLPRS)) {
    return null;
  }

  const payoutRatePercentGuardrailField = `payout_rate_percent_${key}_guardrail`;
  const marginPercentGuardrailField = `base_margin_percent_${key}_guardrail`;
  const incrementalMarginPercentField = `incremental_margin_percent_${key}`;

  if (checkAccess('view', resource, marginPercentGuardrailField) && isMPRS) {
    fields.push(
      <PercentField
        key={marginPercentGuardrailField}
        source={`settings.${settings_subhash_key}.${marginPercentGuardrailField}`}
        label={`Margin Percent ${key.toUpperCase()} Guardrail`}
      />,
    );
  }

  if (checkAccess('view', resource, payoutRatePercentGuardrailField) && isMPRS) {
    fields.push(
      <PercentField
        key={payoutRatePercentGuardrailField}
        source={`settings.${settings_subhash_key}.${payoutRatePercentGuardrailField}`}
        label={`Payout Rate Percent ${key.toUpperCase()} Guardrail`}
      />,
    );
  }

  if (checkAccess('view', resource, incrementalMarginPercentField) && (isMPRS || isLPRS)) {
    fields.push(
      <PercentField
        key={incrementalMarginPercentField}
        source={`settings.${settings_subhash_key}.${incrementalMarginPercentField}`}
        label={`Incremental Margin Percent ${key.toUpperCase()}`}
      />,
    );
  }

  return fields;
};

export const RateSettingsHeading = ({ source, taxClassification }: RateSettingsProps) => {
  const resource = useResourceContext();
  const record = useRecordContext();
  const inheritedRateSettingsDisplayHelper = new RateSettingsDisplayHelper({
    settings: record?.settings,
  });
  const inheritedW2Burdens = inheritedRateSettingsDisplayHelper.getW2Burdens();

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({
    settings: record['settings'],
    location: { ...record?.location },
  });
  const blendedRateCents = rateSettingsDisplayHelper.get12hRate(taxClassification);
  const chargeRateCents = rateSettingsDisplayHelper.getActiveChargeRateCents(taxClassification);
  const isLocationPositionRateSetting = resource === 'LocationPositionRateSetting';
  const isLocationRateSetting = resource === 'LocationRateSetting';
  const isStateLevelPositionRateSetting =
    resource === 'StatePositionRateSetting' || resource === 'StateLocationTypePositionRateSetting';
  const isLocationTypeLevel = resource.includes('LocationType');

  const isLocationLevelRate = isLocationRateSetting || isLocationPositionRateSetting;
  const isBlended = rateSettingsDisplayHelper.getActiveRate(taxClassification) === 'blended-rate';
  const isW2 = taxClassification.startsWith('w2');
  const hideRatesBannerMessage =
    record.location?.primary_billing_group?.plan === 'standard' && isLocationLevelRate;

  const shouldShowField = (field: RateSettingsField): boolean => {
    const isChargeRateField = field === 'charge_rate_8hr' || field === 'charge_rate_12hr';

    if (isLocationPositionRateSetting && isChargeRateField) {
      return isBlended ? field === 'charge_rate_12hr' : field === 'charge_rate_8hr';
    }

    return isFieldVisible(field, resource);
  };

  const getChargeOrPayRateLabel = (rateStructure: '8 Hr' | '12 Hr', isProfessional?: boolean) => {
    const titles = {
      ten99: `1099 ${rateStructure} ${isProfessional ? 'Pro' : 'Charge'} Rate`,
      ['w2.local']: `W2 Local ${rateStructure} ${isProfessional ? 'Pro' : 'Charge'} Rate`,
      ['w2.travel']: `W2 Travel ${rateStructure} ${isProfessional ? 'Pro' : 'Charge'} Rate`,
    };

    return titles[taxClassification];
  };

  return (
    <SimpleShowLayout sx={{ padding: 0, marginBottom: 0 }}>
      {shouldShowField('use_blended_rate') && (
        <UsingBlendedRateField
          forceNestedObject={taxClassification}
          showInheritanceLabel={isLocationLevelRate}
          source={source}
        />
      )}
      {shouldShowField('charge_rate_8hr') && (
        <MoneyField
          source={
            isLocationTypeLevel
              ? `with_markup_settings.${taxClassification}.charge_rate_cents`
              : calculatedFieldPath(`${source}.${taxClassification}`, 'charge_rate_cents', (path) =>
                  get(record, path),
                )
          }
          label={`${getChargeOrPayRateLabel('8 Hr')} ${isBlended ? '' : '(Active)'}`}
          blankIfNull
        />
      )}

      {shouldShowField('charge_rate_12hr') && (
        <FunctionField
          label={`${getChargeOrPayRateLabel('12 Hr')} ${isBlended ? '(Active)' : ''}`}
          render={({ with_markup_settings }) => {
            if (isLocationTypeLevel) {
              const [taxClassificationKey, subHash] = taxClassification.split('.');
              const classificationSettings = subHash
                ? with_markup_settings?.[taxClassificationKey]?.[subHash]
                : with_markup_settings?.[taxClassificationKey];
              const fetchedBlended = classificationSettings?.charge_blended_rate_cents;
              return fetchedBlended ? DisplayHelpers.money(fetchedBlended) : '';
            }

            if (!blendedRateCents || chargeRateCents === blendedRateCents) {
              return '';
            }

            return DisplayHelpers.money(blendedRateCents);
          }}
        />
      )}

      {shouldShowField('pro_rate') && (
        <ProRateField
          label={getChargeOrPayRateLabel(isBlended ? '12 Hr' : '8 Hr', true)}
          nestedOperator={taxClassification.includes('w2') ? 'w2' : 'ten99'}
        />
      )}

      {RateSettingsSpecificFields({
        shouldShowField,
        source,
        taxClassification,
        w2Burdens: inheritedW2Burdens,
      })}

      {ScalingMarginsFields(taxClassification)}

      {!isStateLevelPositionRateSetting && !hideRatesBannerMessage && (
        <Heading>
          <>Overtime Settings</>
          {isW2 && (
            <Alert
              title=""
              description="W2 local and travel will share OT settings. To update, please edit using the W2 local tab"
              variant="filled"
              hideCloseIcon
            />
          )}
        </Heading>
      )}
      {shouldShowField('payout_max_weekly_regular_hours') && (
        <NumberField
          source={`${source}.${taxClassification}.payout_max_weekly_regular_hours`}
          label="Payout Max Weekly Regular Hours"
        />
      )}

      {shouldShowField('payout_seventh_day_rule_enabled') && (
        <BooleanField
          source={`${source}.${taxClassification}.payout_seventh_day_rule_enabled`}
          label="Payout Seventh Day Rule Enabled"
        />
      )}

      {shouldShowField('charge_max_weekly_regular_hours') && (
        <NumberField
          source={`${source}.${taxClassification}.charge_max_weekly_regular_hours`}
          label="Charge Max Weekly Regular Hours"
        />
      )}

      {shouldShowField('charge_seventh_day_rule_enabled') && (
        <BooleanField
          source={`${source}.${taxClassification}.charge_seventh_day_rule_enabled`}
          label="Charge Seventh Day Rule Enabled"
        />
      )}
    </SimpleShowLayout>
  );
};
